<template>
    <main>
        <page-header-compact>
            Listado de historias clinicas
        </page-header-compact>

        <div class="container-fluid px-4 mt-2">
            <!-- Filter Section: Organized 4-Column Grid -->
            <div class="card mb-4 border-0 shadow-sm">
                <div class="card-header bg-transparent border-bottom-0 pt-4 pb-0">
                    <div class="d-flex align-items-center">
                        <i class="fas fa-filter text-gray-400 mr-2"></i>
                        <h5 class="mb-0 font-weight-700 text-gray-700">Búsqueda y Filtros</h5>
                    </div>
                </div>
                <div class="card-body pt-3">
                    <!-- Form: Enter en cualquier campo dispara la búsqueda -->
                    <form class="row align-items-end" @submit.prevent="getHistoriaClinica()">
                        <!-- Row 1: Patient & Basic Filter -->
                        <div class="form-group col-lg-3 col-md-6 mb-3">
                            <label class="small font-weight-700 text-gray-600 mb-1">Documento Usuario</label>
                            <input type="number" class="form-control form-control-sm border-gray-300" v-model="pagination.params.usuario"/>
                        </div>
                        <div class="form-group col-lg-2 col-md-6 mb-3">
                            <label class="small font-weight-700 text-gray-600 mb-1">No. Historia</label>
                            <input class="form-control form-control-sm border-gray-300" type="number" v-model="pagination.params.id_historia"/>
                        </div>
                        <div class="form-group col-lg-3 col-md-6 mb-3">
                            <label class="small font-weight-700 text-gray-600 mb-1">Tipo de Atención</label>
                            <select v-model="pagination.params.id_tipo_historia" class="form-control form-control-sm border-gray-300">
                                <option value="">Todos los tipos</option>
                                <option :value="item.id" v-for="item in tipos_historias" :key="item.id">{{ item.nombre }}</option>
                            </select>
                        </div>
                        <div class="form-group col-lg-4 col-md-6 mb-3">
                            <label class="small font-weight-700 text-gray-600 mb-1">Rango de Fechas</label>
                            <date-picker class="w-100" required v-model="range_fechas" range valueType="format" placeholder="Seleccione fechas"></date-picker>
                        </div>

                        <!-- Row 2: System Filters & Actions -->
                        <div class="form-group col-lg-3 col-md-6 mb-3">
                            <label class="small font-weight-700 text-gray-600 mb-1">Profesional</label>
                            <v-select-user v-model="pagination.params.id_user"></v-select-user>
                        </div>
                        <div class="form-group col-lg-3 col-md-6 mb-3">
                            <label class="small font-weight-700 text-gray-600 mb-1">Sede / Ubicación</label>
                            <VSelectSede v-model="pagination.params.id_sede"></VSelectSede>
                        </div>
                        <div class="form-group col-lg-3 col-md-6 mb-3">
                            <label class="small font-weight-700 text-gray-600 mb-1">EPS</label>
                            <v-select-eps v-model="pagination.params.id_eps"></v-select-eps>
                        </div>
                        <div class="form-group col-lg-3 col-md-12 mb-3 text-right">
                            <button type="submit" class="btn btn-primary btn-sm px-4 mr-2 shadow-none" :disabled="buscando">
                                <i class="fas fa-search mr-2"></i> Buscar
                            </button>
                            <button type="button" class="btn btn-light btn-sm px-4 text-gray-600 border-gray-300 shadow-none font-weight-700" @click="resetFilters()">
                                <i class="fas fa-broom mr-2"></i> Limpiar
                            </button>
                        </div>
                    </form>
                </div>
            </div>


            <div class="card">
                <div class="card-body">
                    <div v-show="!pagination.data.length" class="alert alert-dark  border-0 mb-4">
                        <div class="d-flex align-items-center py-2">
                            <i class="fas fa-search-minus mr-3 opacity-50" style="font-size: 1.25rem;"></i>
                            <div>
                                <div class="font-weight-800 uppercase tracking-wider small mb-1">Sin resultados :(</div>
                                <div class="small opacity-75">No hemos encontrado historias clínicas con los filtros aplicados.</div>
                            </div>
                        </div>
                    </div>
                    <div>
                        <data-grid :data="pagination" @changePage="changePage($event)"
                            @perPageChange="perPageChange($event)" :searchBox="false">
                            <template #grid>
                                <div class="card mb-3 shadow-sm border-left-lg overflow-hidden"
                                    v-for="(item, i) in pagination.data" :key="`d_${i}`"
                                    :class="{ 'border-left-success': item.finalizada === 'S', 'border-left-warning': item.finalizada === 'N' }">
                                   
                                    <!-- Card Header: Reorganized Property Bar -->
                                    <!-- Card Header: High-Density Compact Layout with Separators -->
                                    <div class="card-body py-3 px-4">
                                        <div class="row no-gutters align-items-center">
                                            <!-- Primary ID -->
                                            <div class="col-md-6 col-sm-12 col-xs-12 col-lg-auto d-flex align-items-center mr-lg-4 mb-2 mb-lg-0">
                                                <span class="text-xs text-gray-600 font-weight-700 uppercase tracking-wider mr-2">No.</span>
                                                <span class="h5 mb-0 font-weight-500 text-primary">{{ item.id }}</span>
                                            </div>
                                            
                                            <!-- Type Badge -->
                                            <div class="col-md-6 col-sm-12 col-xs-12 col-lg-auto d-flex align-items-center mb-2 mb-lg-0 responsive-border pl-lg-4 mr-lg-4">
                                                <span class="badge badge-secondary-soft badge-marketing text-gray-700 font-weight-700 border-0 shadow-none px-3" style="font-size: 0.65rem;">
                                                    {{ item.tipo_historia.nombre }}
                                                </span>
                                            </div>

                                            <!-- Date Info -->
                                            <div class="col-md-6 col-sm-12 col-xs-12 col-lg-auto d-flex align-items-center responsive-border pl-lg-4 mr-lg-4 text-gray-600">
                                                <i class="far fa-calendar-alt mr-2 opacity-50"></i>
                                                <span class="small font-weight-600">{{ item.created_at | formatDateTime }}</span>
                                            </div>

                                            <!-- Status Info -->
                                            <div class="col-md-6 col-sm-12 col-xs-12 col-lg-auto d-flex align-items-center responsive-border pl-lg-4">
                                                <div :class="item.finalizada === 'S' ? 'text-success' : 'text-warning'" class="small font-weight-700 d-flex align-items-center">
                                                    <i class="fas fa-circle mr-2" style="font-size: 0.45rem; opacity: 0.6;"></i>
                                                    {{ item.finalizada === 'S' ? 'Finalizada' : 'Sin Finalizar' }}
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    <div class="card-body py-1 px-4">
                                        <!-- Identity Section -->
                                        <div class="mb-2">
                                            <h4 class="font-weight-400 mb-1 tracking-tighter">
                                                <a class="text-dark cursor-pointer link-hover" @click="profile(item.usuario.id)">
                                                    {{ item.usuario.nombre_1 }} {{ item.usuario.nombre_2 }} {{ item.usuario.apellido_1 }} {{ item.usuario.apellido_2 }}
                                                </a>
                                            </h4>
                                            <div class="d-flex flex-wrap align-items-center" style="gap: 10px;">
                                                <span class="text-dark font-weight-400">{{ item.usuario.tipo_documento.codigo }} {{ item.usuario.documento }}</span>
                                                <span class="badge badge-cyan-soft text-black font-weight-600 px-2">
                                                    {{ getYears(item.usuario.fecha_nacimiento, item.fecha) }}
                                                </span>
                                                <span class="text-muted font-weight-700">
                                                    <i class="fas fa-hospital-alt mr-1"></i>
                                                    {{ item.eps ? item.eps.DESEPS : 'SIN EPS' }}
                                                </span>
                                            </div>
                                        </div>

                                        <hr class="my-3" style="border-color: #e3e6ec;">

                                        <!-- Documentation Actions: Desaturated Icons -->
                                        <div class="mb-2">
                                            <div class="text-xs font-weight-800 text-gray-500 uppercase mb-2" style="font-size: 0.55rem;">Acciones Clínicas</div>
                                            <div class="d-flex flex-wrap" style="gap: 4px;">
                                                <button @click="imprimir(item.id);" class="btn btn-xs btn-light text-gray-600 font-weight-700 border-gray-200">
                                                    <i class="fas fa-print text-primary fa-fw"></i>H.C Completa
                                                </button>
                                                <button @click="resumen(item.id);" class="btn btn-xs btn-light text-gray-600 font-weight-700 border-gray-200">
                                                    <i class="fas fa-file-invoice text-indigo fa-fw"></i>{{ item.tipo_historia.codigo!=='URG' ? 'Resumen' : 'Epicrisis' }}
                                                </button>
                                                <button v-if="item.formulas_count" @click="formulaModal(item.id);" class="btn btn-xs btn-light text-gray-600 font-weight-700 border-gray-200">
                                                    <i class="fas fa-pills text-red fa-fw"></i>Fórmulas
                                                </button>
                                                <button v-if="item.remisiones_count" @click="abrirModal(item.id)" class="btn btn-xs btn-light text-gray-600 font-weight-700 border-gray-200">
                                                    <i class="fas fa-share-square text-teal fa-fw"></i>Remisiones
                                                </button>
                                                <button v-if="item.evoluciones_count" @click="EvolucionModal(item.id)" class="btn btn-xs btn-light text-gray-600 font-weight-700 border-gray-200">
                                                    <i class="fas fa-history text-orange fa-fw"></i>Evoluciones
                                                </button>
                                                <button v-if="item.incapacidades_count" @click="IncapacidadModal(item.id)" class="btn btn-xs btn-light text-gray-600 font-weight-700 border-gray-200">
                                                    <i class="fas fa-user-md text-cyan fa-fw"></i>Incapacidades
                                                </button>
                                                <button @click="openModalNotasEnfermeria(item.id_usuario)" class="btn btn-xs btn-light text-gray-600 font-weight-700 border-gray-200">
                                                    <i class="fas fa-notes-medical text-purple fa-fw"></i>Notas Enfermeria
                                                </button>
                                                <button v-if="item.ordenes_count" @click="ordenModal(item.id, 'procedimiento');" class="btn btn-xs btn-light text-gray-600 font-weight-700 border-gray-200">
                                                    <i class="fas fa-clipboard-list text-pink fa-fw"></i>Ord. Procedimientos
                                                </button>
                                                <button v-if="item.ordenes_count && item.tipo_historia.codigo === 'URG'" @click="ordenModal(item.id, 'medicamento');" class="btn btn-xs btn-light text-gray-600 font-weight-700 border-gray-200">
                                                    <i class="fas fa-prescription-bottle-alt text-green fa-fw"></i>Ord. Medicas
                                                </button>
                                            </div>
                                        </div>
                                        
                                        <div class="mb-2">
                                            <VictimArmedConflict v-show="Number(item.usuario.victima_conflicto) === 1"></VictimArmedConflict>
                                        </div>
                                    </div>

                                    <!-- Footer: Metadata & Admin Actions -->
                                    <div class="card-footer bg-transparent border-top-0 pt-0 pb- d-flex align-items-center justify-content-between px-4">
                                        <div class="d-flex flex-wrap align-items-center x-small font-weight-700 text-gray-600 uppercase tracking-tighter" style="font-size: 0.7rem;">
                                            <span class="mr-3"><i class="fas fa-user-md fa-fw"></i> {{ item.user.name }}</span>
                                            <span><i class="fas fa-map-marker-alt fa-fw"></i> {{ item.sede ? item.sede.NOMBRE_SEDE : 'SIN SEDE' }}</span>
                                        </div>
                                        
                                        <div class="d-flex btn-group flex-wrap">
                                            <button class="btn btn-xs btn-outline-success px-4 shadow-none transition-all"
                                                    v-if="_can('open.hc') && (item.finalizada === 'S' || item.ingreso === 'finalizado')"
                                                    @click="openModalHC(item)" title="Abrir Historia">
                                                    <i class="fas fa-folder-open mr-2"></i> Abrir HC
                                                </button>
                                            <button v-if="_can('change.state.hc')" @click="cambiar(item.id)" class="btn btn-xs btn-outline-secondary">
                                                <i class="fas fa-edit fa-fw"></i> Cambiar Estado
                                            </button>
                                            <button v-if="_can('change.eps.hc')" @click="openModalChangeEps(item)" class="btn btn-xs btn-outline-warning">
                                                <i class="fas fa-exchange-alt fa-fw"></i> Cambiar EPS
                                            </button>
                                            <button v-if="_can('change.date.hc')" @click="openModalCambiarFecha(item)"  class="btn btn-xs btn-outline-dark">
                                                <i class="fas fa-calendar-alt fa-fw"></i> Cambiar Fecha
                                            </button>
                                            <button v-if="_can('anular.hc')" @click="openModalAnularHC(item)" class="btn btn-xs btn-outline-danger">
                                                <i class="fas fa-ban fa-fw"></i> Anular HC
                                            </button>
                                            
                                        </div>
                                    </div>
                                </div>
                            </template>
                        </data-grid>
                        <modal-remisiones ref="modalRemisiones"></modal-remisiones>
                        <modal-ordenes ref="modalOrdenes"></modal-ordenes>
                        <modal-formulas ref="modalFormulas"></modal-formulas>
                        <modal-evoluciones ref="modalEvoluciones"></modal-evoluciones>
                        <modal-incapacidades ref="modalIncapacidades"></modal-incapacidades>
                        <modalOpenHC ref="modalHC"></modalOpenHC>
                        <modalChangeEps ref="modalEps" v-on:cambio-eps="getHistoriaClinica()"></modalChangeEps>
                        <modalAnularHc ref="modalAHc" v-on:anular-hc="getHistoriaClinica()"></modalAnularHc>
                        <modalNotasEnfermeria ref="modalNotasEnfermeria"></modalNotasEnfermeria>
                        <modal-cambiar-fecha-hc ref="modalCambiarFecha" @fecha-cambiada="getHistoriaClinica()"></modal-cambiar-fecha-hc>
                    </div>
                </div>
            </div>
        </div>
    </main>
</template>
<script>
import PageHeaderCompact from "../../../components/layouts/content/page-header-compact";
import DataGrid from "../../../components/common/utilities/DataGrid/DataGrid";
import Toast from '../../../components/common/utilities/toast';
import vSelectUser from "../../../components/common/vSelectUser";
import DatePicker from 'vue2-datepicker';
import 'vue2-datepicker/index.css';
import VSelectSede from '../../../components/common/VSelectSede';
import historiaClinicaService from '../../../services/historiaClinicaService';
import modalRemisiones from "../base/baseHistorialHistoria/modalRemisiones";
import modalOrdenes from "../base/baseHistorialHistoria/modalOrdenes";
import modalFormulas from "../base/baseHistorialHistoria/modalFormulas";
import modalEvoluciones from "../base/baseHistorialHistoria/modalEvoluciones";
import modalIncapacidades from "../base/baseHistorialHistoria/modalIncapacidades";
import modalOpenHC from "../base/baseHistorialHistoria/abrirHistoria/modalOpenHC";
import modalChangeEps from "./changeEps";
import modalAnularHc from "./anularHc";
import modalCambiarFechaHc from "./cambiarFechaHc";
import tipoHistoriaService from "../../../services/tipoHistoriaService";
import dayjs from 'dayjs';
import modalNotasEnfermeria from "./modalNotasEnfermeria";
import VictimArmedConflict from "../../../components/common/alerts/VictimArmedConflict.vue";
import VSelectEps from "../../../components/common/VSelectEps";
export default {
    components: {
        PageHeaderCompact, DataGrid,
        vSelectUser, VSelectSede, DatePicker, modalRemisiones, modalOrdenes, modalFormulas, modalEvoluciones, modalIncapacidades,
        modalOpenHC, modalChangeEps, modalAnularHc, modalCambiarFechaHc, modalNotasEnfermeria, VictimArmedConflict, VSelectEps
    },
    data() {
        return {
            range_fechas: '',
            buscando: false,
            pagination: {
                data: [],
                params: {
                    fecha: '',
                    id_user: '',
                    usuario: "",
                    id_tipo_historia: "",
                    id_sede: "",
                    id_historia: "",
                    id_eps: "",
                },
                current_page: 1,
                page: 1,
                per_page: 5,
                search: '',
                total: '',
            },
            tipos_historias: []
        }
    },

    methods: {
        async getHistoriaClinica() {
            // Evita búsquedas encimadas si se presiona Enter repetidamente.
            if (this.buscando) return;

            try {
                this.buscando = true;
                this.LoaderSpinnerShow();
                this.pagination.params.start_date = this.range_fechas[0];
                this.pagination.params.end_date = this.range_fechas[1];

                const response = (await historiaClinicaService.showByHC(this.pagination.search, this.pagination))?.data;

                this.pagination.data = response.data;
                this.pagination.current_page = response.current_page;
                this.pagination.total = response.total;
            } catch (error) {
                console.log(error);
                Toast.fire({
                    icon: "error",
                    title: "Ha ocurrido un error al procesar la solicitud",
                });
            } finally {
                // finally garantiza que el spinner y el bloqueo siempre se liberen.
                this.LoaderSpinnerHide();
                this.buscando = false;
            }

        },
        imprimir(id) {
            window.open(historiaClinicaService.print(id));
        },
        resumen(id) {
            window.open(historiaClinicaService.resumen(id));
        },
        abrirModal(id_historia) {
            this.$refs.modalRemisiones.abrir(id_historia);
        },
        formulaModal(id_historia) {
            this.$refs.modalFormulas.abrir(id_historia);
        },
        ordenModal(id_historia, type) {
            this.$refs.modalOrdenes.abrir(id_historia, type);
        },
        EvolucionModal(id_historia) {
            this.$refs.modalEvoluciones.abrir(id_historia);
        },
        IncapacidadModal(id_historia) {
            this.$refs.modalIncapacidades.abrir(id_historia);
        },
        openModalNotasEnfermeria(idUsuario) {
            this.$refs.modalNotasEnfermeria.openModal(idUsuario);
        },
        async cambiar(id) {
            try {
                this.LoaderSpinnerShow();
                await historiaClinicaService.cambiarEstado(id);
                this.LoaderSpinnerHide();
                Toast.fire({
                    icon: 'success',
                    title: 'Estado cambiado exitosamente'
                });
                this.getHistoriaClinica();
            } catch (e) {
                console.error(e);
                this.LoaderSpinnerHide();
                Toast.fire({
                    icon: 'error',
                    title: 'Ocurrio un error al procesar la solicitud'
                });
            }
        },
        changePage(page) {
            this.pagination.page = page;
            this.getHistoriaClinica();
        },
        perPageChange(per_page) {
            this.pagination.per_page = per_page;
            this.pagination.page = 1;
            this.getHistoriaClinica();
        },
        search(query) {
            this.pagination.search = query;
            this.pagination.page = 1;
            this.getHistoriaClinica();
        },
        openModalHC(historia) {
            this.$refs.modalHC.openModal(historia);
        },
        openModalChangeEps(historia) {
            this.$refs.modalEps.openModal(historia);
        },
        openModalAnularHC(historia) {
            this.$refs.modalAHc.openModal(historia);
        },
        openModalCambiarFecha(historia) {
            this.$refs.modalCambiarFecha.openModal(historia);
        },
        resetFilters() {
            this.pagination.params.id_user = "";
            this.pagination.params.start_date = "";
            this.pagination.params.end_date = "";
            this.pagination.params.id_sede = "";
            this.pagination.params.id_tipo_historia = "";
            this.pagination.params.id_historia = "";
            this.range_fechas = {};
            this.pagination.params.usuario = "";
            this.pagination.data = [];
            this.pagination.total = 0;
            this.pagination.params.id_eps = "";
            
        },
        async getTiposHistorias() {
            const response = await tipoHistoriaService.index();
            this.tipos_historias = response.data;
        },
        getYears(fechaNacimiento, fechaAtencion) {
            const fechaNacimientoDayjs = dayjs(fechaNacimiento);
            const diaAtencionDayjs = dayjs(fechaAtencion);

            const años = diaAtencionDayjs.diff(fechaNacimientoDayjs, 'year');

            // Ajustar la fecha de nacimiento al año de la fecha de atención
            const fechaNacimientoConAños = fechaNacimientoDayjs.add(años, 'year');

            // Calcular la diferencia en meses
            const meses = diaAtencionDayjs.diff(fechaNacimientoConAños, 'month');

            // Ajustar la fecha de nacimiento al mes de la fecha de atención
            const fechaNacimientoConMeses = fechaNacimientoConAños.add(meses, 'month');

            // Calcular la diferencia en días
            const días = diaAtencionDayjs.diff(fechaNacimientoConMeses, 'day');

            return `${años} años ${meses} meses ${días} dias`;
        },
        profile(id){
            
            const url = this.$router.resolve({
                name: 'historias.perfil.usuario',
                params: { idUsuario: id }
            }).href;

            window.open(url, '_blank');
        }
    },
    async created() {
        await this.getTiposHistorias();
    }

}
</script>
<style scoped>
    .cursor-pointer{
        cursor: pointer;
    }
    .link-hover:hover {
        color: #0061f2 !important;
        text-decoration: none;
    }
    @media (min-width: 992px) {
        .responsive-border {
            border-left: 1px solid #e3e6ec;
            padding-left: 1.5rem;
            margin-left: 0.5rem;
        }
    }
</style>

